import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { DISABLED_SUBTITLES_ID } from '@bedrock-rdk/media-engine';
import { t } from '../../../i18n';
import { SELECTED_SIGNAL } from '../../../tornado/signals';
import { TracksPanel } from './tracksPanel/tracksPanel.component';
import connect from './tracks.connector';
import { CLOSE_SIGNAL, AUDIO_TRACK_SELECTED_SIGNAL, SUBTITLES_TRACK_SELECTED_SIGNAL } from './tracks.constants';
import { getInitialStyle, defaultContext, getUnFocusedStyle, getFocusedStyle } from './tracks.style';
import { formatAudioTracks, formatSubtitlesTracks } from './tracks.utils';
import { isForcedSubtitlesTrack } from '../../../../logic/player/player.utils';

export class Tracks extends Lightning.Component {
  static _template() {
    return _merge(
      {
        rect: true,
        BackgroundOverlay: {
          rect: true,
        },
        Panels: {
          AudioTracks: {
            type: TracksPanel,
            title: t('player.audio'),
            signals: {
              [SELECTED_SIGNAL]: '_handleAudioTrackSelection',
            },
          },
          SubtitleTracks: {
            type: TracksPanel,
            title: t('player.subtitles'),
            signals: {
              [SELECTED_SIGNAL]: '_handleSubtitlesTrackSelection',
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
    this._reset();
  }

  _attach() {
    this._unsubscribe = connect(this);
    this._panels = [this.tag('AudioTracks'), this.tag('SubtitleTracks')];
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this._reset();
  }

  set audioTracks(value) {
    this._setPanelTracks(this.tag('AudioTracks'), formatAudioTracks(value));
    this.tag('AudioTracks').setFocusToSelectedItem();
  }

  set subtitleTracks(value) {
    const selectableSubtitles = (value || []).filter(track => !isForcedSubtitlesTrack(track));
    this._subtitles = formatSubtitlesTracks(selectableSubtitles);
    this._setPanelTracks(this.tag('SubtitleTracks'), this._subtitles);
  }

  set selectedAudioTrack(value) {
    this.tag('AudioTracks').highlightPreferredTrack(value);
  }

  set selectedSubtitlesTrack(value) {
    const id = value || Number.isFinite(value) ? value : DISABLED_SUBTITLES_ID;
    const track = this._subtitles && this._subtitles.find(({ id }) => id);

    this.tag('SubtitleTracks').highlightPreferredTrack(track ? id : null);
  }

  _setPanelTracks(panel, tracks) {
    panel.visible = Boolean(tracks.length);
    panel.items = tracks;

    this._focused = this._panels.find(panel => panel.visible);
    this._refocus();
  }

  _unfocus() {
    this.patch(getUnFocusedStyle(this._style.screenSize));

    // Ensure last selected audio track will be in focus by default
    this.tag('AudioTracks').setFocusToSelectedItem();
    // Ensure focus will move seamlessly to the Subtitles panel
    this.tag('SubtitleTracks').index = 0;
    // Drop focus to the top-most visible panel(usually it's Audio)
    this._focused = this._panels.find(panel => panel.visible);
  }

  _focus() {
    this.patch(getFocusedStyle(this._style.screenSize));
  }

  _fireCloseSignal() {
    this.signal(CLOSE_SIGNAL);

    return true;
  }

  _getFocused() {
    return this._focused;
  }

  _handleBack() {
    return this._fireCloseSignal();
  }

  _handleLeft() {
    return this._fireCloseSignal();
  }

  _handleUp() {
    const panel = this.tag('AudioTracks');
    if (panel.visible) {
      this._focused = panel;
    }

    return true;
  }

  _handleDown() {
    const panel = this.tag('SubtitleTracks');
    if (panel.visible) {
      this._focused = panel;
    }

    return true;
  }

  _handleAudioTrackSelection(track) {
    this.signal(AUDIO_TRACK_SELECTED_SIGNAL, (track && track.id) || null);
  }

  _handleSubtitlesTrackSelection(track) {
    const id = (track && track.id) || null;
    this.signal(SUBTITLES_TRACK_SELECTED_SIGNAL, id === DISABLED_SUBTITLES_ID ? null : id);
  }

  _reset() {
    this._focused = null;
    this._selectedProfile = null;
    this._subtitles = undefined;
    this._panels = [];
  }
}
